import WorkspaceInput from '@components/Common/WorkspaceInput';
import FormField from '@components/Common/FormField';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import {
  TOKEN_STATE_META,
  classifyTokens,
  isValidTokenName,
  type TokenInfo,
  type TokenState,
} from './tokens';
import './index.scss';

interface PlaceholderManagerProps {
  /** The authored text tokens are parsed from. */
  body: string;
  /** Names in availableVariables. */
  declared: string[];
  /** Names the server resolves itself; read-only, never declared. */
  autoVars: string[];
  /** One sample value per token, driving live preview. */
  sampleValues: Record<string, string>;

  onDeclaredChange: (next: string[]) => void;
  onSampleValueChange: (name: string, value: string) => void;
  /** Rewrites every occurrence in the body. */
  onRename: (from: string, to: string) => void;
  /** Removes the declaration (and, for `unused`, nothing else). */
  onRemove: (name: string) => void;
  /** Insert at the caret of the field the author last touched. */
  onInsert?: (name: string) => void;
}

const STATE_ORDER: TokenState[] = ['undeclared', 'declared', 'auto', 'unused'];

/**
 * Reconciles declared placeholders against the ones actually used, and makes
 * declaration a consequence of use rather than a second list to maintain.
 *
 * Four states, colour-coded identically here and in the body:
 *   declared    --acc    in availableVariables AND used
 *   auto-filled --teal   resolved server-side
 *   undeclared  --red    used but not declared  -> publish blocker
 *   unused      --grey   declared but absent    -> one-click remove
 */
const PlaceholderManager: React.FC<PlaceholderManagerProps> = ({
  body,
  declared,
  autoVars,
  sampleValues,
  onDeclaredChange,
  onSampleValueChange,
  onRename,
  onRemove,
  onInsert,
}) => {
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameTo, setRenameTo] = useState('');
  const [renameError, setRenameError] = useState('');

  const tokens = useMemo(
    () => classifyTokens(body, declared, autoVars),
    [body, declared, autoVars],
  );

  const grouped = useMemo(() => {
    const map = new Map<TokenState, TokenInfo[]>();
    for (const state of STATE_ORDER) map.set(state, []);
    for (const t of tokens) map.get(t.state)!.push(t);
    return map;
  }, [tokens]);

  const undeclared = grouped.get('undeclared')!;
  const unused = grouped.get('unused')!;

  const declareAll = () => {
    onDeclaredChange([...declared, ...undeclared.map((t) => t.name)]);
  };

  const beginRename = (name: string) => {
    setRenaming(name);
    setRenameTo(name);
    setRenameError('');
  };

  const commitRename = () => {
    if (!renaming) return;
    const next = renameTo.trim();
    if (next === renaming) return setRenaming(null);
    if (!isValidTokenName(next)) {
      return setRenameError('Letters, numbers and underscore only.');
    }
    if (autoVars.includes(next)) {
      return setRenameError(`"${next}" is auto-filled. Pick another name.`);
    }
    if (declared.includes(next)) {
      return setRenameError(`"${next}" already exists.`);
    }
    onRename(renaming, next);
    setRenaming(null);
  };

  const renderChip = (t: TokenInfo) => {
    const meta = TOKEN_STATE_META[t.state];
    const isRenaming = renaming === t.name;

    if (isRenaming) {
      return (
        <Box key={t.name} className="cc-ph__rename">
          <WorkspaceInput
            value={renameTo}
            error={!!renameError}
            helperText={renameError}
            onChange={(v) => {
              setRenameTo(v);
              setRenameError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') setRenaming(null);
            }}
          />
          <IconButton size="small" onClick={commitRename} aria-label="Confirm rename">
            <CheckCircleOutlineIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => setRenaming(null)} aria-label="Cancel rename">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }

    return (
      <Box
        key={t.name}
        className="cc-ph__chip"
        style={{ backgroundColor: meta.tint, borderColor: meta.line, color: meta.tone }}
      >
        {/* A disabled button inside a Tooltip never fires the events the
            Tooltip listens for, so when there is nowhere to insert we render
            static text rather than a button that cannot be pressed. */}
        <Tooltip title={onInsert ? 'Insert at cursor' : meta.help}>
          {onInsert ? (
            <button
              type="button"
              className="cc-ph__chip-name"
              onClick={() => onInsert(t.name)}
            >
              {t.name}
              {t.count > 1 && <span className="cc-ph__chip-count">×{t.count}</span>}
            </button>
          ) : (
            <span className="cc-ph__chip-name">
              {t.name}
              {t.count > 1 && <span className="cc-ph__chip-count">×{t.count}</span>}
            </span>
          )}
        </Tooltip>

        {/* Auto-filled names are the server's; they are not the author's to
            rename or remove. */}
        {t.state !== 'auto' && (
          <>
            <Tooltip title="Rename everywhere">
              <IconButton
                size="small"
                className="cc-ph__chip-action"
                onClick={() => beginRename(t.name)}
                aria-label={`Rename ${t.name}`}
              >
                <EditOutlinedIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t.state === 'unused' ? 'Remove declaration' : 'Remove everywhere'}>
              <IconButton
                size="small"
                className="cc-ph__chip-action"
                onClick={() => onRemove(t.name)}
                aria-label={`Remove ${t.name}`}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box className="cc-ph">
      {tokens.length === 0 && (
        <Typography className="cc-ph__empty">
          No placeholders yet. Type <code>{'{{'}</code> in the body to insert one.
        </Typography>
      )}

      {STATE_ORDER.map((state) => {
        const list = grouped.get(state)!;
        if (list.length === 0) return null;
        const meta = TOKEN_STATE_META[state];
        return (
          <Box key={state} className="cc-ph__section">
            <Box className="cc-ph__section-head">
              <span className="cc-ph__swatch" style={{ backgroundColor: meta.tone }} />
              <span className="cc-ph__section-label" style={{ color: meta.tone }}>
                {meta.label}
              </span>
              <span className="cc-ph__section-count">{list.length}</span>
              {state === 'undeclared' && (
                <button type="button" className="cc-ph__section-action" onClick={declareAll}>
                  Declare all
                </button>
              )}
              {state === 'unused' && (
                <button
                  type="button"
                  className="cc-ph__section-action"
                  onClick={() => onDeclaredChange(declared.filter((d) => !unused.some((u) => u.name === d)))}
                >
                  Remove all
                </button>
              )}
            </Box>
            <Typography className="cc-ph__section-help">{meta.help}</Typography>
            <Box className="cc-ph__chips">{list.map(renderChip)}</Box>

            {/* Sample values drive the live preview. Auto-filled tokens are
                resolved by the server, so there is nothing to sample. */}
            {state === 'declared' && (
              <Box className="cc-ph__samples">
                {list.map((t) => (
                  <FormField
                    required={false}
                    key={t.name}
                    name={t.name}
                    label={t.name}
                    placeholder="Sample value"
                    value={sampleValues[t.name] ?? ''}
                    onChange={(_n, v) => onSampleValueChange(t.name, String(v))}
                  />
                ))}
              </Box>
            )}
          </Box>
        );
      })}

      {undeclared.length > 0 && (
        <Box className="cc-ph__blocker">
          <ErrorOutlineIcon sx={{ fontSize: 15 }} />
          <span>
            {undeclared.length} undeclared placeholder{undeclared.length === 1 ? '' : 's'} would
            render empty. This blocks publish.
          </span>
        </Box>
      )}
    </Box>
  );
};

export default PlaceholderManager;
